import { Component, EventEmitter, Input, Output, OnChanges, OnInit, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Observable } from 'rxjs';

import { Abstract } from '../../../../models/abstract/abstract.model';
import { AbstractSubmission } from '../../../../models/abstract/abstract-submission.model';
import { Note } from '../../../../models/evaluation/note.model';
import { SubmissionType, Roles, AbstractStatus } from '../../../../models/enums';

import { DecisionBottomSheetComponent } from '../../../bottom-sheet/decision-bottom-sheet/decision-bottom-sheet.component';
import { QuickNoteDialogComponent } from '../../../dialogs/quick-note-dialog/quick-note-dialog.component';

import { AbstractService } from '../../../../services/abstract.service';
import { AccountService } from '../../../../services/account.service';
import { AlertService, MessageSeverity } from '../../../../services/alert.service';
import { AuthService } from '../../../../services/auth.service';
import { NoteService } from '../../../../services/note.service';
import { Utilities } from '../../../../services/utilities';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';


@Component({
  selector: 'app-view-abstract',
  templateUrl: './view-abstract.component.html',
  styleUrls: ['../abstract-container.component.css', './view-abstract.component.css'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    RouterModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ViewAbstractComponent implements OnInit, OnChanges {
  @Output() editing = new EventEmitter();
  @Input() abstract: Abstract;
  @Input() userName: string;
  @Input() fullName: string;

  get submissionTypes() { return SubmissionType; }

  canSeeEdit: boolean;
  canSeeOverride: boolean;
  canSeeCode: boolean;
  canSeeReadOnlyFlags: boolean;
  readOnly = false;
  reconSupCoding = false;

  submissionId: number = -1;
  submissionLinks: Array<object> = [];

  activeRole: Roles;
  activeProject: number = 0;
  activeProjProtocol: number = 0;
  notes: Note[];

  isQcOverride = false;
  isRecOverride = false;

  overrideButtonText = "Abstract Override";
  overrideConfirmText = "Are you sure you want to override this abstract?";

  constructor(
    private accountService: AccountService,
    private abstractService: AbstractService,
    private authService: AuthService,
    private alertService: AlertService,
    private noteService: NoteService,
    private bottomSheet: MatBottomSheet,
    private dialog: MatDialog,
    public router: Router,
    public activatedRoute: ActivatedRoute
  ) { }

  codeAbstract() {
    if (this.submissionId === -1) {
      this.checkIfAbstractBeingCoded(this.abstract.id, this.activeProjProtocol);
    } else {
      this.router.navigate([`evaluation/view/${this.submissionId}`]);
    }
  }

  checkIfAbstractBeingCoded(abstractId: number, protocolId: number) {
    this.alertService.startLoadingMessage();
    if (this.accountService.GetActiveTeam() && this.accountService.GetActiveTeam().teamId > 0) {
      console.log("User has active team to check against: " + this.accountService.GetActiveTeam().teamId);
      this.abstractService.getOpenAbstract(this.accountService.GetActiveRole(), this.accountService.GetActiveTeam().teamId, abstractId).subscribe({
        next: abstractInCoding => {
          this.alertService.stopLoadingMessage();
          if (abstractInCoding && abstractInCoding.abstractId &&
            (abstractInCoding.abstractId !== abstractId || abstractInCoding.protocolId !== protocolId)) {

            const message = 'A team member has already opened another abstract for coding. ' +
              `You will be redirected to abstract "${abstractInCoding.abstract.projectTitle}".`;

            this.openBottomSheet('Abstract Coding In Progress', message).subscribe(isOk => {
              if (isOk) {
                this.router.navigate([`/abstract/view/${abstractInCoding.abstractId}`]);
              }
            });
          } else {
            this.router.navigate([`evaluation/code/${this.abstract.id}/${this.activeProjProtocol}`]);
          }
        },
        error: (error) => {
          this.alertService.stopLoadingMessage();
          this.alertService.showStickyMessage(
            'Load Error',
            `Unable to retrieve open abstract from server.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
            MessageSeverity.error,
            error);
        }});
    }
    else {
      this.router.navigate([`evaluation/code/${this.abstract.id}/${this.abstract.protocolId}`]);
    }
  }

  overrideAbstract() {
    this.openBottomSheet('Confirm Override', this.overrideConfirmText).subscribe({
      next: confirmed => {
        if (confirmed) {
          this.abstractService.getOverrideAbstract(this.abstract.id, this.abstract.protocolId, this.accountService.GetActiveRole()).subscribe({
            next: _ => {
              this.alertService.showMessage('Override successful', 'Redirecting to home page.', MessageSeverity.success);
              this.router.navigate([`home/actions`]);
            },
            error: (error) => {
              const httpError = Utilities.getHttpResponseMessage(error);
              this.alertService.showStickyMessage(
                'Override error',
                `Unable to override abstract.\r\nErrors: "${httpError}"`,
                MessageSeverity.error,
                error
              );
            }
          });
        }
    }});
  }

  formatSubmissionString(sub: AbstractSubmission) {
    switch (sub.submissionTypeId) {
      case SubmissionType.OdpWestatComparison:
        return "Reconciliation Comparison with QC-Consensus and ML [O3]";
      case SubmissionType.OdpConsensus:
        return "Reconciliation Coding Consensus [O2]";
      case SubmissionType.CoderMlComparison:
        return "QC Comparison with ML [W3]";
      case SubmissionType.CoderConsensus:
        return "QC Coding Consensus [W2]";
      case SubmissionType.MlEvaluation:
        return "ML Selections";
      case SubmissionType.CoderEvaluation:
        return `Individual QC Coding by ${sub.fullName}`;
      case SubmissionType.OdpEvaluation:
        return `Individual Rec. Coding by ${sub.fullName}`;
      default:
        return `ID: ${sub.fullName} Selections`;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.readOnly && changes['abstract'] && this.abstract != null) {
      this.noteService.getNotesByAbstractProtocolId(this.abstract.id, this.abstract.protocolId, this.activeRole)
        .subscribe(notes => this.notes = notes);

      if (this.abstract.submissions != null && this.abstract.submissions.length > 0) {
        console.log(this.abstract.submissions);
        const reverseSubs = this.abstract.submissions.reverse();

        for (let i = 0; i < reverseSubs.length; i++) {
          this.submissionLinks.push({ title: this.formatSubmissionString(reverseSubs[i]), id: reverseSubs[i].id, dateString: reverseSubs[i].submissionDateString });
        }
      }
      this.reconSupCoding = this.activatedRoute.snapshot.queryParamMap.get('reconsupcoding') === 'true' ? true : false;

      var canAttemptTeamId = (this.activeRole == Roles.QCCoder || this.activeRole == Roles.ReconciliationCoder || (this.activeRole == Roles.ReconciliationSupervisor && this.reconSupCoding)) ? this.accountService.GetActiveTeam().teamId : 0;
      this.canSeeReadOnlyFlags = this.activeRole == Roles.QCSupervisor || 
        this.activeRole == Roles.ReconciliationSupervisor || 
        this.activeRole == Roles.Admin || 
        this.activeRole == Roles.QCCoder;
      
      this.abstractService.canUserAttemptCodeAbstract(this.abstract.id, this.abstract.protocolId, this.accountService.GetActiveRole(), canAttemptTeamId != null ? canAttemptTeamId : 0)
        .subscribe({
        next: response => {
            this.canSeeOverride = response.canOverride;
            // is on team for this abstract/it's codable?
            // adding check for returnedFromGetNextAbstract to ensure that the "Code Abstract" button only shows when an abstract is pulled from GetNextAbstract, not when navigating to an abstract from search results
            this.canSeeCode = response.canEvaluate && this.abstract.returnedFromGetNextAbstract;
            this.submissionId = response.submissionId;

            if (response.abstractStatusId == AbstractStatus.RETRIEVED_FOR_CODING_BY_CODER_1
              || response.abstractStatusId == AbstractStatus.READY_FOR_CONSENSUS_BY_CODERS_1A
              || (response.abstractStatusId == AbstractStatus.CONSENSUS_COMPLETED_BY_CODERS_1B && response.hasMlCoding)) {
              this.isQcOverride = true
            }
            else if ((response.abstractStatusId == AbstractStatus.CONSENSUS_COMPLETED_BY_CODERS_1B && !response.hasMlCoding)
              || (response.abstractStatusId == AbstractStatus.MLCOMPARE_COMPLETED_BY_CODERS_1ML && response.hasMlCoding)
              || response.abstractStatusId == AbstractStatus.CONSENSUS_COMPLETE_NOTES_UPLOADED_BY_CODERS_1N
              || response.abstractStatusId == AbstractStatus.RETRIEVED_FOR_CODING_BY_ODP_STAFF_2
              || response.abstractStatusId == AbstractStatus.READY_FOR_CONSENSUS_BY_ODP_STAFF_2A
              || response.abstractStatusId == AbstractStatus.CONSENSUS_COMPLETED_BY_ODP_STAFF_2B) {
              this.isRecOverride = true;
            }
            if (this.isQcOverride) {
              this.overrideButtonText = "Abstract Override (QC)";
              this.overrideConfirmText = "<span>This will reset the abstract to <b>QC Coding Not Started</b>. Are you sure you want to override this abstract?</span>"
            }
            else if (this.isRecOverride) {
              this.overrideButtonText = "Abstract Override (Recon.)";
              this.overrideConfirmText = "<span>This will reset the abstract to <b>Reconciliation Coding Not Started</b>. Are you sure you want to override this abstract?</span>"
            }
        },
        error: (error) => {
            const httpError = Utilities.getHttpResponseMessage(error);
            this.alertService.showStickyMessage(
              'Status Error',
              `Unable to retrieve abstract status from the server.\r\nErrors: "${httpError}"`,
              MessageSeverity.error,
              error
            );
          }
        });
    }
  }

  ngOnInit() {
    if (this.authService.isSessionExpired) {
      console.log(`VIEW ABSTRACT: REDIRECT TO LOGIN`);
      this.router.navigateByUrl('/account/login');
      return;
    }

    this.activeRole = this.accountService.GetActiveRole();
    if (this.accountService.GetActiveProject()) {
      this.activeProject = this.accountService.GetActiveProject().odpProjectWorkflowId;
      this.activeProjProtocol = this.accountService.GetActiveProject().protocolId;
    }
    this.readOnly = this.activatedRoute.snapshot.params['readonly'] ? true : false;
    this.reconSupCoding = this.activatedRoute.snapshot.queryParamMap.get('reconsupcoding') === 'true' ? true : false;

    this.canSeeOverride = false;
    this.canSeeCode = false;
    this.canSeeEdit = !this.readOnly && (this.activeRole === Roles.Admin || this.activeRole === Roles.ReconciliationSupervisor);
  }

  setEdit() {
    this.editing.emit();
  }

  openDialog() {
    this.dialog.open(QuickNoteDialogComponent, {
      width: '450px',
      data: { notes: this.notes }
    });
  }

  openBottomSheet(sheetTitle: string, sheetText: string): Observable<any> {
    return this.bottomSheet.open(DecisionBottomSheetComponent, {
      data: {
        sheetTitle: sheetTitle,
        sheetHtml: sheetText
      },
      closeOnNavigation: true
    }).afterDismissed();
  }
}
